/**
 * Tool UI for show_files: the agent's finished files, in the chat (MediaGallery).
 */
import { makeAssistantToolUI } from '@assistant-ui/react';
import { FolderOpen, XCircle } from 'lucide-react';
import { ToolRow, plainText, wasInterrupted, INTERRUPTED_META } from '../toolRow';
import { ShownFiles, type ShownFile } from './MediaGallery';
import { useOffloadedResult, type EnvelopeFields } from './useOffloadedResult';

interface ShowFilesArgs {
  files?: Array<{ path: string; caption?: string }>;
  _thought?: string;
}

interface ShowFilesResult {
  files?: ShownFile[];
  missing?: string[];
  error?: string;
}

export const ShowFilesToolUI = makeAssistantToolUI<ShowFilesArgs, ShowFilesResult>({
  toolName: 'show_files',
  render: function ShowFilesRender({ args, result: raw, status }) {
    // Long captions can take the result past the envelope's inline limit.
    const offloaded = useOffloadedResult<ShowFilesResult>(raw as EnvelopeFields | undefined);
    const result = offloaded.data ?? raw;
    const n = args.files?.length ?? 0;
    const label = n === 1 ? 'Showing a file' : n ? `Showing ${n} files` : 'Showing files';

    if (result?.files?.length) return <ShownFiles files={result.files} missing={result.missing} />;
    if (result?.error) {
      return (
        <ToolRow
          icon={<XCircle className="text-red-500" />}
          label={label}
          title={result.error}
          meta={<span className="text-red-600">Failed</span>}
          tone="error"
          open={false}
        />
      );
    }
    return <ToolRow icon={<FolderOpen />} label={plainText(args._thought) || label} running={status?.type === 'running'} meta={wasInterrupted(status, raw) ? INTERRUPTED_META : undefined} open={false} />;
  },
});
